// @ts-nocheck
/**
 * TeamLeader Workspace — Training Management Page
 * Answers: Who is behind? Who completed? Who failed? Who has pending modules/checkpoints?
 * Filters by Group, Track, Course, and Status.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchMyGroups,
  fetchAllAssignableTracks,
  fetchAllAssignableCourses,
} from "@/lib/api/teamleader";
import { fetchStudentGrid, fetchStudentStatusCounts } from "@/lib/api/students";
import { PaginationControls } from "@/components/PaginationControls";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BookOpen,
  GraduationCap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/training")({
  component: TeamLeaderTrainingPage,
});

function TeamLeaderTrainingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [trackFilter, setTrackFilter] = useState("all");

  // Queries
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Queries
  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ["tl", "groups"],
    queryFn: fetchMyGroups,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // The flat, walk-every-page variants: this dropdown wants the whole catalogue, and the paged
  // fetchers would hand back a PagedResult envelope rather than an array.
  const { data: tracks = [] } = useQuery({
    queryKey: ["tl", "assignable-tracks"],
    queryFn: fetchAllAssignableTracks,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ["tl", "assignable-courses"],
    queryFn: fetchAllAssignableCourses,
  });

  const { data: statusCounts } = useQuery({
    queryKey: ["tl", "student-status-counts"],
    queryFn: fetchStudentStatusCounts,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const groupMap = useMemo(() => {
    return new Map(groups.map((g) => [g.id, g]));
  }, [groups]);

  // Map metric cards
  const behindCount = statusCounts ? (statusCounts.blacklisted || 0) + (statusCounts.notResponding || 0) : 0;
  const activeCount = statusCounts ? (statusCounts.total || 0) - behindCount : 0;

  // Build grid query params
  const gridParams = useMemo(() => {
    const params: any = {
      pageNumber: page,
      pageSize: pageSize,
      search: search.trim(),
    };

    if (groupFilter !== "all") {
      params.groupId = groupFilter;
    }

    if (statusFilter === "behind") {
      params.isBlacklisted = true;
    } else if (statusFilter === "completed") {
      params.trainingStatus = ["Passed"];
    }

    return params;
  }, [page, pageSize, search, groupFilter, statusFilter]);

  const { data: gridData, isLoading: studentsLoading } = useQuery({
    queryKey: ["tl", "students-grid-training", gridParams],
    queryFn: () => fetchStudentGrid(gridParams),
    enabled: !!user?.id,
  });

  const students = gridData?.items || [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Training Management</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Track progress, module completions, behind learners, and pending checkpoints across your team
        </p>
      </div>

      {/* Overview Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-warning">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">Behind / At Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-warning">{behindCount}</span>
              <AlertTriangle className="h-5 w-5 text-warning" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Learners requiring intervention</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">On Track / Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-success">{activeCount}</span>
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Actively learning</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">Assigned Tracks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-primary">{tracks.length}</span>
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Available learning tracks</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search learner..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>

        <select
          className="h-10 px-3 py-2 border rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Training Statuses</option>
          <option value="behind">Behind / At Risk</option>
          <option value="completed">Completed</option>
        </select>

        <select
          className="h-10 px-3 py-2 border rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          value={groupFilter}
          onChange={(e) => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      {/* Training Roster List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Training Roster Status</CardTitle>
          <CardDescription>Individual training breakdown for led students</CardDescription>
        </CardHeader>
        <CardContent>
          {groupsLoading || studentsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : students.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">No students match current training filters.</p>
          ) : (
            <>
              <div className="grid gap-3">
                {students.map((s: any) => {
                  const primaryGroup = s.groups?.[0];
                  const groupName = primaryGroup?.groupName || "Unassigned";
                  const groupInfo = primaryGroup ? groupMap.get(primaryGroup.groupId) : null;
                  const groupTracks = groupInfo?.tracks?.map((t) => t.name) || [];
                  const isAtRisk = s.isBlacklisted || s.isNotResponding;

                  return (
                    <div
                      key={s.id}
                      className="p-4 border rounded-xl bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                          {(s.fullName || s.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{s.fullName || s.email}</p>
                          <p className="text-xs text-muted-foreground">
                            {groupName} · {groupTracks.join(", ") || "No Track"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Badge variant={isAtRisk ? "destructive" : "secondary"}>
                          {isAtRisk ? "Behind" : s.trainingStatus || "Active"}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate({
                              to: "/team-leader/students/$studentId",
                              params: { studentId: s.id },
                            })
                          }
                        >
                          View Progress
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {gridData && (
                <PaginationControls
                  pageNumber={page}
                  pageSize={pageSize}
                  totalPages={gridData.totalPages}
                  totalCount={gridData.totalCount}
                  hasNextPage={gridData.hasNextPage}
                  hasPreviousPage={gridData.hasPreviousPage}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  isLoading={studentsLoading}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
